/**
 * SEO Component
 * 
 * Manages document head meta tags for SEO and social sharing.
 * Uses React hooks to update document.head on mount/update.
 */

import { useEffect } from 'react';

/**
 * Default meta values
 */
const DEFAULT_META = {
  title: "The Pirate's Guild",
  description: "Join The Pirate's Guild community! Find business discounts, housing, and connect with fellow traveling swashbucklers.",
  image: '/assets/og-image.png',
  url: 'https://thepirates-guild.web.app',
  type: 'website',
  siteName: "The Pirate's Guild"
};

/**
 * Page-specific meta configurations
 */
export const PAGE_META = {
  home: {
    title: "The Pirate's Guild - Community for Traveling Swashbucklers",
    description: "Join The Pirate's Guild community! Find exclusive business discounts, housing options, and connect with fellow traveling swashbucklers."
  },
  listings: {
    title: "Business Discounts - The Pirate's Guild",
    description: "Browse exclusive business discounts and offers for traveling swashbucklers. Save on housing, dining, shopping, and more."
  },
  roommate: {
    title: "Roommate Finder - The Pirate's Guild",
    description: "Find compatible roommates for your next port of call. Connect with verified traveling mateys from every walk of life."
  },
  tokens: {
    title: "Tokens & Products - The Pirate's Guild",
    description: "Top up Doubloons for bonus loyalty perks and sponsored partner rewards across the U.S. Virgin Islands."
  },
  account: {
    title: "Account Settings - The Pirate's Guild",
    description: "Manage your Pirate's Guild account settings, profile, and preferences."
  },
  help: {
    title: "Help & Support - The Pirate's Guild",
    description: "Get help with your Pirate's Guild account. Browse FAQs, contact support, and find answers to common questions."
  },
  admin: {
    title: "Admin Dashboard - The Pirate's Guild",
    description: "Administrative dashboard for managing The Pirate's Guild platform."
  }
};

/**
 * Update meta tag content or create new tag
 */
function setMetaTag(name, content, attribute = 'name') {
  if (!content) return;
  
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    element.setAttribute('content', content);
    document.head.appendChild(element);
  }
}

/**
 * Update link tag or create new link
 */
function setLinkTag(rel, href) {
  if (!href) return;
  
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (element) {
    element.setAttribute('href', href);
  } else {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute('href', href);
    document.head.appendChild(element);
  }
}

/**
 * SEO Hook - Updates document meta tags
 * 
 * @param {Object} options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.image - OG image URL
 * @param {string} options.url - Canonical URL
 * @param {string} options.type - OG type (website, article, etc.)
 * @param {Object} options.article - Article-specific metadata
 */
export function useSEO({
  title,
  description,
  image,
  url,
  type = 'website',
  article,
  noindex = false
} = {}) {
  useEffect(() => {
    const fullTitle = title || DEFAULT_META.title;
    const fullDescription = description || DEFAULT_META.description;
    const fullImage = image ? 
      (image.startsWith('http') ? image : `${DEFAULT_META.url}${image}`) : 
      `${DEFAULT_META.url}${DEFAULT_META.image}`;
    const fullUrl = url ? 
      (url.startsWith('http') ? url : `${DEFAULT_META.url}${url}`) : 
      DEFAULT_META.url;

    // Update document title
    document.title = fullTitle;

    // Primary meta tags
    setMetaTag('description', fullDescription);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    setMetaTag('og:title', fullTitle, 'property');
    setMetaTag('og:description', fullDescription, 'property');
    setMetaTag('og:image', fullImage, 'property');
    setMetaTag('og:url', fullUrl, 'property');
    setMetaTag('og:type', type, 'property');
    setMetaTag('og:site_name', DEFAULT_META.siteName, 'property');

    // Twitter
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', fullDescription);
    setMetaTag('twitter:image', fullImage);
    setMetaTag('twitter:url', fullUrl);

    // Article-specific
    if (type === 'article' && article) {
      setMetaTag('article:published_time', article.publishedTime, 'property');
      setMetaTag('article:modified_time', article.modifiedTime, 'property');
      setMetaTag('article:author', article.author, 'property');
      setMetaTag('article:section', article.section, 'property');
    }

    // Canonical URL
    setLinkTag('canonical', fullUrl);

  }, [title, description, image, url, type, article, noindex]);
}

/**
 * SEO Component - Functional wrapper around useSEO hook
 * 
 * Usage:
 * <SEO 
 *   title="Page Title" 
 *   description="Page description"
 * />
 */
export function SEO(props) {
  useSEO(props);
  return null;
}

/**
 * Generate structured data for a business listing (JSON-LD)
 * 
 * @param {Object} listing - Business listing data
 * @returns {Object} JSON-LD structured data
 */
export function generateBusinessSchema(listing) {
  if (!listing) return null;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": listing.name,
    "description": listing.description,
    "address": listing.location ? {
      "@type": "PostalAddress",
      "addressLocality": listing.location
    } : undefined,
    "telephone": listing.phone,
    "url": listing.website,
    "image": listing.images?.[0],
    "priceRange": listing.priceRange,
    "openingHours": listing.hours,
    "aggregateRating": listing.rating ? {
      "@type": "AggregateRating",
      "ratingValue": listing.rating,
      "ratingCount": listing.reviewCount || 1
    } : undefined
  };
}

/**
 * Generate structured data for the organization (JSON-LD)
 */
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The Pirate's Guild",
    "url": "https://thepirates-guild.web.app",
    "logo": "https://thepirates-guild.web.app/assets/logo.png",
    "description": "Community for traveling swashbucklers offering exclusive business discounts, housing resources, and professional connections.",
    "sameAs": [
      // Add social media URLs when available
    ]
  };
}

/**
 * Generate breadcrumb structured data (JSON-LD)
 * 
 * @param {Array} items - Array of breadcrumb items [{name, url}]
 */
export function generateBreadcrumbSchema(items) {
  if (!items || items.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `https://thepirates-guild.web.app${item.url}` : undefined
    }))
  };
}

/**
 * StructuredData Component - Injects JSON-LD into document
 * 
 * @param {Object} data - JSON-LD structured data object
 */
export function StructuredData({ data }) {
  useEffect(() => {
    if (!data) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
}

export default SEO;
